import React from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { API_ENDPOINTS } from '../../config/api'
import {
  fetchDataFlows,
  createDataFlow,
  deleteDataFlow,
  downloadCsvExport,
  type DataFlowRow,
} from '../../services/informationSystemPhase13Api'
import { DATA_CATEGORY_CATALOG_FALLBACK } from '../../services/informationSystemsApi'
import type { InformationSystemSummary } from '../../services/informationSystemsApi'
import styles from '../../styles/components/assets/AssetsDataMap.module.scss'

interface Props {
  systems: InformationSystemSummary[]
  catalog: string[]
  onChanged: () => void
  /** When set, only flows where this system is source or target are listed. */
  filterSystemId?: string
}

export default function DataMapDataFlowsSection({ systems, catalog, onChanged, filterSystemId }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [dataFlows, setDataFlows] = React.useState<DataFlowRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [flowSource, setFlowSource] = React.useState('')
  const [flowTarget, setFlowTarget] = React.useState('')
  const [flowCategory, setFlowCategory] = React.useState('')
  const [flowType, setFlowType] = React.useState('API')
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  const categoryOptions = catalog.length > 0 ? catalog : DATA_CATEGORY_CATALOG_FALLBACK
  const canAdd =
    systems.length > 0 &&
    Boolean(flowSource) &&
    Boolean(flowTarget) &&
    Boolean(flowCategory) &&
    flowSource !== flowTarget

  const loadFlows = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const flows = await fetchDataFlows(token, orgId)
      setDataFlows(Array.isArray(flows) ? flows : [])
    } finally {
      setLoading(false)
    }
  }, [token, orgId])

  React.useEffect(() => {
    loadFlows()
  }, [loadFlows])

  const visibleFlows = React.useMemo(() => {
    if (!filterSystemId) return dataFlows
    return dataFlows.filter(
      (f) => f.sourceSystemId === filterSystemId || f.targetSystemId === filterSystemId,
    )
  }, [dataFlows, filterSystemId])

  return (
    <div className={styles.intelligenceCard}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('assets.dataFlows.title', 'Data flows')}</h3>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={async () => {
            if (!token) return
            const csv = await downloadCsvExport(token, API_ENDPOINTS.INFORMATION_SYSTEMS_DATA_MAP_EXPORT_CSV)
            const blob = new Blob([csv], { type: 'text/csv' })
            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = 'data-map.csv'
            a.click()
          }}
        >
          {t('assets.export.dataMap', 'Export data map CSV')}
        </button>
      </div>
      <div className={styles.cardBody}>
        {loading ? <p className={styles.loadingText}>{t('common.loading', 'Loading…')}</p> : null}
        {!loading && dataFlows.length === 0 ? (
          <p className={styles.widgetEmpty}>{t('assets.dataFlows.empty', 'No data flows defined yet.')}</p>
        ) : null}
        {!loading && dataFlows.length > 0 && filterSystemId && visibleFlows.length === 0 ? (
          <p className={styles.widgetEmpty}>
            {t('assets.dataMap.emptyFlowsForSystem', 'No data flows for this information system as source or target.')}
          </p>
        ) : null}
        {!loading && visibleFlows.length > 0 ? (
          <ul className={styles.flowList}>
            {visibleFlows.map((f) => (
              <li key={f.id} className={styles.flowItem}>
                <p className={styles.flowItemText}>
                  {f.sourceSystem?.name || f.sourceSystemId} → {f.targetSystem?.name || f.targetSystemId}:{' '}
                  {f.dataCategory} ({f.flowType})
                </p>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={async () => {
                    if (!token) return
                    await deleteDataFlow(token, f.id, orgId)
                    await loadFlows()
                    onChanged()
                  }}
                >
                  {t('common.remove', 'Remove')}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className={styles.flowFormGrid} style={{ marginTop: '0.75rem' }}>
          <select className={styles.selectField} value={flowSource} onChange={(e) => setFlowSource(e.target.value)} style={{ maxWidth: '100%', width: '100%' }}>
            <option value="">{t('assets.dataFlows.source', 'Source system')}</option>
            {systems.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select className={styles.selectField} value={flowTarget} onChange={(e) => setFlowTarget(e.target.value)} style={{ maxWidth: '100%', width: '100%' }}>
            <option value="">{t('assets.dataFlows.target', 'Target system')}</option>
            {systems.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select className={styles.selectField} value={flowCategory} onChange={(e) => setFlowCategory(e.target.value)} style={{ maxWidth: '100%', width: '100%' }}>
            <option value="">{t('assets.dataMap.category', 'Data category')}</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select className={styles.selectField} value={flowType} onChange={(e) => setFlowType(e.target.value)} style={{ maxWidth: '100%', width: '100%' }}>
            {['API', 'DATABASE_REPLICATION', 'FILE_TRANSFER', 'MESSAGE_QUEUE', 'MANUAL', 'OTHER'].map((ft) => (
              <option key={ft} value={ft}>
                {t(`assets.dataFlows.flowType.${ft}`, ft)}
              </option>
            ))}
          </select>
        </div>
        {systems.length === 0 ? (
          <p className={styles.widgetEmpty}>
            {t(
              'assets.dataFlows.needSystems',
              'Create at least one information system on the Information Systems tab before adding data flows.'
            )}
          </p>
        ) : null}
        {!canAdd && systems.length > 0 ? (
          <p className={styles.widgetEmpty}>
            {t(
              'assets.dataFlows.fillAllFields',
              'Select source system, target system, and data category (source and target must differ).'
            )}
          </p>
        ) : null}
        {submitError ? <p className={styles.errorBanner}>{submitError}</p> : null}
        <button
          type="button"
          className={styles.primaryBtn}
          style={{ marginTop: '0.5rem' }}
          disabled={!canAdd}
          onClick={async () => {
            if (!token || !canAdd) return
            setSubmitError(null)
            try {
              await createDataFlow(
                token,
                {
                  sourceSystemId: flowSource,
                  targetSystemId: flowTarget,
                  dataCategory: flowCategory,
                  flowType,
                  direction: 'OUTBOUND',
                },
                orgId
              )
              setFlowSource('')
              setFlowTarget('')
              setFlowCategory('')
              await loadFlows()
              onChanged()
            } catch (e: unknown) {
              setSubmitError(e instanceof Error ? e.message : t('assets.dataFlows.errorAdd', 'Failed to add data flow'))
            }
          }}
        >
          {t('assets.dataFlows.add', 'Add data flow')}
        </button>
      </div>
    </div>
  )
}
